import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { usersAPI, systemAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw, Copy, Settings, X } from 'lucide-react';

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
  });

  const [configData, setConfigData] = useState({
    gtt1_upside: 5.0,
    gtt1_downside: 3.0,
    gtt2_upside: 7.0,
    gtt2_downside: 3.0,
    default_position_size: 5000,
    require_trade_approval: false,
    prompt_on_increase_exposure: false,
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.list().then((res) => res.data),
  });

  // Fetch ngrok URL
  const { data: ngrokData } = useQuery({
    queryKey: ['ngrok-url'],
    queryFn: () => systemAPI.getNgrokUrl().then((res) => res.data),
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: false, // Don't retry if ngrok is not available
  });

  // Auto-open config modal if config query param is present
  useEffect(() => {
    const configUserId = searchParams.get('config');
    if (configUserId && users) {
      const user = users.find((u: any) => u.id === parseInt(configUserId));
      if (user) {
        openConfigModal(user);
        // Remove query param after opening
        setSearchParams({});
      }
    }
  }, [searchParams, users]);

  const createMutation = useMutation({
    mutationFn: usersAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created successfully');
      setShowForm(false);
      setFormData({ username: '', email: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: usersAPI.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User status updated');
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: ({ userId, config }: { userId: number; config: any }) =>
      usersAPI.updateConfig(userId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Configuration updated successfully');
      setShowConfigModal(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update configuration');
    },
  });

  const regenerateTokenMutation = useMutation({
    mutationFn: ({ userId, tokenType }: { userId: number; tokenType: string }) =>
      usersAPI.regenerateToken(userId, tokenType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Webhook tokens regenerated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: usersAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const openConfigModal = (user: any) => {
    setSelectedUser(user);
    const config = user.webhook_config || {};
    setConfigData({
      gtt1_upside: config.gtt1?.upside_percent || 5.0,
      gtt1_downside: config.gtt1?.downside_percent || 3.0,
      gtt2_upside: config.gtt2?.upside_percent || 7.0,
      gtt2_downside: config.gtt2?.downside_percent || 3.0,
      default_position_size: config.default_position_size || 5000,
      require_trade_approval: config.require_trade_approval || false,
      prompt_on_increase_exposure: config.prompt_on_increase_exposure || false,
    });
    setShowConfigModal(true);
  };

  const handleSaveConfig = () => {
    const config = {
      gtt1: {
        upside_percent: parseFloat(configData.gtt1_upside.toString()),
        downside_percent: parseFloat(configData.gtt1_downside.toString()),
      },
      gtt2: {
        upside_percent: parseFloat(configData.gtt2_upside.toString()),
        downside_percent: parseFloat(configData.gtt2_downside.toString()),
      },
      default_position_size: parseInt(configData.default_position_size.toString()),
      require_trade_approval: configData.require_trade_approval,
      prompt_on_increase_exposure: configData.prompt_on_increase_exposure,
    };

    updateConfigMutation.mutate({ userId: selectedUser.id, config });
  };

  const getWebhookUrl = (user: any, type: string) => {
    const urls = user.webhook_urls || {};
    return urls[type] || '';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-gray-600">Manage users and their webhook endpoints</p>
            {ngrokData?.ngrok_available ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                🌐 Public (ngrok)
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                🏠 Local only
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Add User
        </button>
      </div>

      {/* Add User Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Add New User</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <input
                type="text"
                required
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                placeholder="trader1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                placeholder="trader1@example.com"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create User'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      {isLoading ? (
        <div className="text-center py-8">Loading users...</div>
      ) : users && users.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No users yet. Create your first user to get started!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {users?.map((user: any) => (
            <div key={user.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {user.username}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{user.email}</p>

                  {/* Webhook URLs */}
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-700">Webhook Endpoints:</h4>
                      {ngrokData?.ngrok_available && (
                        <span className="text-xs text-green-600 font-medium">
                          ✅ Public URLs (accessible from internet)
                        </span>
                      )}
                    </div>

                    {/* BUY Webhook */}
                    <div className="bg-green-50 p-3 rounded border border-green-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-green-800">BUY Webhook</span>
                        <button
                          onClick={() =>
                            copyToClipboard(getWebhookUrl(user, 'buy'), 'BUY webhook URL')
                          }
                          className="text-green-600 hover:text-green-900"
                          title="Copy BUY webhook URL"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <code className="text-xs bg-white px-2 py-1 rounded block overflow-x-auto">
                        {getWebhookUrl(user, 'buy')}
                      </code>
                    </div>

                    {/* SELL Webhook */}
                    <div className="bg-red-50 p-3 rounded border border-red-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-red-800">SELL Webhook</span>
                        <button
                          onClick={() =>
                            copyToClipboard(getWebhookUrl(user, 'sell'), 'SELL webhook URL')
                          }
                          className="text-red-600 hover:text-red-900"
                          title="Copy SELL webhook URL"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <code className="text-xs bg-white px-2 py-1 rounded block overflow-x-auto">
                        {getWebhookUrl(user, 'sell')}
                      </code>
                    </div>

                    {/* GTT1 Webhook */}
                    <div className="bg-blue-50 p-3 rounded border border-blue-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-blue-800">
                          GTT1 Webhook ({user.webhook_config?.gtt1?.upside_percent || 5}% up, {user.webhook_config?.gtt1?.downside_percent || 3}% down)
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(getWebhookUrl(user, 'gtt1'), 'GTT1 webhook URL')
                          }
                          className="text-blue-600 hover:text-blue-900"
                          title="Copy GTT1 webhook URL"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <code className="text-xs bg-white px-2 py-1 rounded block overflow-x-auto">
                        {getWebhookUrl(user, 'gtt1')}
                      </code>
                    </div>

                    {/* GTT2 Webhook */}
                    <div className="bg-purple-50 p-3 rounded border border-purple-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-purple-800">
                          GTT2 Webhook ({user.webhook_config?.gtt2?.upside_percent || 7}% up, {user.webhook_config?.gtt2?.downside_percent || 3}% down)
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(getWebhookUrl(user, 'gtt2'), 'GTT2 webhook URL')
                          }
                          className="text-purple-600 hover:text-purple-900"
                          title="Copy GTT2 webhook URL"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <code className="text-xs bg-white px-2 py-1 rounded block overflow-x-auto">
                        {getWebhookUrl(user, 'gtt2')}
                      </code>
                    </div>

                    {/* TradingView Webhook */}
                    {getWebhookUrl(user, 'tradingview') && (
                    <div className="bg-green-50 p-3 rounded border border-green-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-green-800">
                          TradingView Webhook (Alpaca)
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(getWebhookUrl(user, 'tradingview'), 'TradingView webhook URL')
                          }
                          className="text-green-600 hover:text-green-900"
                          title="Copy TradingView webhook URL"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <code className="text-xs bg-white px-2 py-1 rounded block overflow-x-auto">
                        {getWebhookUrl(user, 'tradingview')}
                      </code>
                    </div>
                    )}
                  </div>

                  {/* Position Size Summary */}
                  {user.webhook_config?.default_position_size && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Default Position Size:
                      </h4>
                      <span className="bg-gray-100 px-3 py-1 rounded text-sm">
                        <strong>₹{user.webhook_config.default_position_size}</strong> per stock
                      </span>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="mt-4 flex gap-4 text-sm text-gray-600">
                    <span>Created: {new Date(user.created_at).toLocaleDateString()}</span>
                    {user.last_webhook_at && (
                      <span>
                        Last webhook: {new Date(user.last_webhook_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => openConfigModal(user)}
                    className="text-purple-600 hover:text-purple-900 p-2"
                    title="Configure webhooks"
                  >
                    <Settings size={20} />
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          'Regenerate ALL webhook tokens? This will invalidate all old tokens!'
                        )
                      ) {
                        regenerateTokenMutation.mutate({ userId: user.id, tokenType: 'all' });
                      }
                    }}
                    className="text-orange-600 hover:text-orange-900 p-2"
                    title="Regenerate all tokens"
                  >
                    <RefreshCw size={20} />
                  </button>
                  <button
                    onClick={() => toggleMutation.mutate(user.id)}
                    className="text-blue-600 hover:text-blue-900 p-2"
                    title="Toggle active"
                  >
                    {user.is_active ? (
                      <ToggleRight size={20} />
                    ) : (
                      <ToggleLeft size={20} />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          'Delete this user and all their accounts? This cannot be undone!'
                        )
                      ) {
                        deleteMutation.mutate(user.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900 p-2"
                    title="Delete user"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">Configure Webhooks - {selectedUser.username}</h2>
              <button
                onClick={() => setShowConfigModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* GTT1 Configuration */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <h3 className="font-semibold text-lg mb-3 text-blue-900">GTT1 Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upside %
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={configData.gtt1_upside}
                      onChange={(e) =>
                        setConfigData({ ...configData, gtt1_upside: parseFloat(e.target.value) })
                      }
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Downside %
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={configData.gtt1_downside}
                      onChange={(e) =>
                        setConfigData({ ...configData, gtt1_downside: parseFloat(e.target.value) })
                      }
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>
              </div>

              {/* GTT2 Configuration */}
              <div className="border rounded-lg p-4 bg-purple-50">
                <h3 className="font-semibold text-lg mb-3 text-purple-900">GTT2 Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upside %
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={configData.gtt2_upside}
                      onChange={(e) =>
                        setConfigData({ ...configData, gtt2_upside: parseFloat(e.target.value) })
                      }
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Downside %
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={configData.gtt2_downside}
                      onChange={(e) =>
                        setConfigData({ ...configData, gtt2_downside: parseFloat(e.target.value) })
                      }
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>
              </div>

              {/* Position Size Configuration */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Default Position Size</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Set a constant amount (in ₹) to invest per stock. This will be used for ALL stocks that come through the webhook.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount per Stock (₹)
                  </label>
                  <input
                    type="number"
                    step="100"
                    min="0"
                    value={configData.default_position_size}
                    onChange={(e) =>
                      setConfigData({ ...configData, default_position_size: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="5000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Example: If you set 5000 and stock price is ₹1000, system will buy 5 shares
                  </p>
                </div>
              </div>

              {/* Trade Approval Mode */}
              <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <span>Trade Approval Mode</span>
                  <span className={`text-xs px-2 py-1 rounded ${configData.require_trade_approval ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'}`}>
                    {configData.require_trade_approval ? 'MANUAL' : 'AUTO'}
                  </span>
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  {configData.require_trade_approval
                    ? '⚠️ Manual mode: Webhook signals will be saved for your review. You must approve each trade before execution.'
                    : '✅ Automatic mode: Webhook signals will be executed immediately without approval.'
                  }
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={configData.require_trade_approval}
                    onChange={(e) =>
                      setConfigData({ ...configData, require_trade_approval: e.target.checked })
                    }
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Require manual approval for all trades
                  </span>
                </label>
                <p className="text-xs text-gray-600 mt-2">
                  {configData.require_trade_approval
                    ? 'You will receive pending trade notifications and can edit parameters before execution.'
                    : 'Trades will execute automatically as soon as webhooks are received.'
                  }
                </p>
              </div>

              {/* Increase Exposure Prompt */}
              <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <span>Position Increase Prompts</span>
                  <span className={`text-xs px-2 py-1 rounded ${configData.prompt_on_increase_exposure ? 'bg-orange-500 text-white' : 'bg-gray-500 text-white'}`}>
                    {configData.prompt_on_increase_exposure ? 'ASK ME' : 'SKIP'}
                  </span>
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  {configData.prompt_on_increase_exposure
                    ? '⬆️ Ask for approval: When a BUY signal arrives for a stock you already hold, you will be asked to approve adding to your position.'
                    : '🚫 Skip duplicates: BUY signals for stocks already in your portfolio will be skipped automatically.'
                  }
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={configData.prompt_on_increase_exposure}
                    onChange={(e) =>
                      setConfigData({ ...configData, prompt_on_increase_exposure: e.target.checked })
                    }
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Ask me before adding to existing positions
                  </span>
                </label>
                <p className="text-xs text-gray-600 mt-2">
                  {configData.prompt_on_increase_exposure
                    ? 'When enabled, you will see current holdings and can decide whether to increase exposure.'
                    : 'BUY signals for stocks you already hold will be ignored to prevent duplicate purchases.'
                  }
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-2 border-t">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={updateConfigMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {updateConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
